import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import devicesQueries from "../../../requests/queries/DevicesQueries";
import ConfirmationModal from "../../common/ConfirmationModal";
import "./devices.css";
import projectsQueries from "../../../requests/queries/ProjectsQuesries";

export default function Devices() {
    const [deviceGroups, setDeviceGroups] = useState([]);
    const [devices, setDevices] = useState([]);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');

    // États pour les modals
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [showDeviceModal, setShowDeviceModal] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [editingDevice, setEditingDevice] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    // États des formulaires
    const [groupForm, setGroupForm] = useState({ name: '', description: '' });
    const [deviceForm, setDeviceForm] = useState({ name: '', description: '', device_group_id: '', project_id: '' });
    const [projects, setProjects] = useState([]);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const [groupsData, devicesData] = await Promise.all([
                devicesQueries.getDeviceGroups(),
                devicesQueries.getDevices()
            ]);
            setDeviceGroups(groupsData || []);
            setDevices(devicesData || []);
            const projectsData = await projectsQueries.getProjects();
            setProjects(projectsData || []);
        } catch (error) {
            console.error('Erreur lors du chargement:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleGroup = (groupId) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupId]: !prev[groupId]
        }));
    };

    const getDevicesByGroup = (groupId) => {
        return devices.filter(device => device.device_group_id === groupId);
    };

    const filteredGroups = deviceGroups.filter(group =>
        group.name.toLowerCase().includes(search.toLowerCase()) ||
        group.description?.toLowerCase().includes(search.toLowerCase())
    );

    // Handlers pour les groupes
    const openAddGroupModal = () => {
        setEditingGroup(null);
        setGroupForm({ name: '', description: '' });
        setShowGroupModal(true);
    };

    const openEditGroupModal = (group) => {
        setEditingGroup(group);
        setGroupForm({ name: group.name, description: group.description || '' });
        setShowGroupModal(true);
    };

    const handleGroupSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingGroup) {
                await devicesQueries.updateDeviceGroup(editingGroup.id, groupForm.name, groupForm.description);
            } else {
                await devicesQueries.createDeviceGroup(groupForm.name, groupForm.description);
            }
            setShowGroupModal(false);
            loadData();
        } catch (error) {
            console.error('Erreur:', error);
        }
    };

    const handleDeleteGroup = async () => {
        if (deleteTarget?.type === 'group') {
            try {
                await devicesQueries.deleteDeviceGroup(deleteTarget.item.id);
                loadData();
            } catch (error) {
                console.error('Erreur:', error);
            }
        }
        setDeleteTarget(null);
    };

    // Handlers pour les devices
    const openAddDeviceModal = (groupId = '') => {
        setEditingDevice(null);
        setDeviceForm({ name: '', description: '', device_group_id: groupId, project_id: '' });
        setShowDeviceModal(true);
    };

    const openEditDeviceModal = (device) => {
        setEditingDevice(device);
        setDeviceForm({
            name: device.name,
            description: device.description || '',
            device_group_id: device.device_group_id || '',
            project_id: device.project_id || ''
        });
        setShowDeviceModal(true);
    };

    const handleDeviceSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingDevice) {
                await devicesQueries.updateDevice(
                    editingDevice.id,
                    deviceForm.name,
                    deviceForm.description,
                    deviceForm.device_group_id,
                    deviceForm.project_id || null
                );
            } else {
                await devicesQueries.createDevice(
                    deviceForm.name,
                    deviceForm.description,
                    deviceForm.device_group_id,
                    deviceForm.project_id || null
                );
            }
            setShowDeviceModal(false);
            loadData();
        } catch (error) {
            console.error('Erreur:', error);
        }
    };

    const handleDeleteDevice = async () => {
        if (deleteTarget?.type === 'device') {
            try {
                await devicesQueries.deleteDevice(deleteTarget.item.id);
                loadData();
            } catch (error) {
                console.error('Erreur:', error);
            }
        }
        setDeleteTarget(null);
    };

    const handleDelete = () => {
        if (deleteTarget?.type === 'group') {
            handleDeleteGroup();
        } else if (deleteTarget?.type === 'device') {
            handleDeleteDevice();
        }
    };

    return (
        <div className="devices-page">
            <div className="devices-container">
                {/* Header */}
                <header className="devices-header">
                    <div className="devices-header-top">
                        <div className="devices-title-group">
                            <h1 className="devices-title">
                                <span className="devices-title-icon">📱</span>
                                Gestion des Devices
                            </h1>
                            <p className="devices-subtitle">
                                Organisez vos bornes et terminaux par groupes
                            </p>
                        </div>

                        <div className="devices-header-actions">
                            <Link to="/projects" className="nav-link-btn">
                                <span>📁</span>
                                Projets
                            </Link>
                            <button className="devices-create-btn secondary" onClick={openAddGroupModal}>
                                <span>🗂️</span>
                                Nouveau groupe
                            </button>
                            <button className="devices-create-btn" onClick={() => openAddDeviceModal()}>
                                <span>➕</span>
                                Nouveau device
                            </button>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="devices-stats">
                        <div className="devices-stat">
                            <span className="devices-stat-icon">📁</span>
                            <span className="devices-stat-value">{deviceGroups.length}</span>
                            <span className="devices-stat-label">groupe{deviceGroups.length > 1 ? 's' : ''}</span>
                        </div>
                        <div className="devices-stat">
                            <span className="devices-stat-icon">📱</span>
                            <span className="devices-stat-value">{devices.length}</span>
                            <span className="devices-stat-label">device{devices.length > 1 ? 's' : ''}</span>
                        </div>
                    </div>

                    {/* Recherche */}
                    <div className="devices-search-bar">
                        <input
                            type="text"
                            className="devices-search-input"
                            placeholder="Rechercher un groupe..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <span className="devices-search-icon">🔍</span>
                    </div>
                </header>

                {/* Liste des groupes */}
                {!isLoading && filteredGroups.length > 0 && (
                    <div className="devices-groups-list">
                        {filteredGroups.map((group) => {
                            const groupDevices = getDevicesByGroup(group.id);
                            const isExpanded = expandedGroups[group.id];

                            return (
                                <div key={group.id} className={`device-group-card ${isExpanded ? 'expanded' : ''}`}>
                                    <div className="device-group-header" onClick={() => toggleGroup(group.id)}>
                                        <div className="device-group-info">
                                            <div className="device-group-icon">📁</div>
                                            <div className="device-group-details">
                                                <h3 className="device-group-name">{group.name}</h3>
                                                <p className="device-group-description">
                                                    {group.description || "Aucune description"}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="device-group-meta">
                                            <span className="device-group-count">
                                                {groupDevices.length} device{groupDevices.length > 1 ? 's' : ''}
                                            </span>
                                            <div className="device-group-actions">
                                                <button
                                                    className="device-group-action-btn edit"
                                                    onClick={(e) => { e.stopPropagation(); openEditGroupModal(group); }}
                                                    title="Modifier le groupe"
                                                >
                                                    ✏️
                                                </button>
                                                <button
                                                    className="device-group-action-btn add"
                                                    onClick={(e) => { e.stopPropagation(); openAddDeviceModal(group.id); }}
                                                    title="Ajouter un device"
                                                >
                                                    ➕
                                                </button>
                                                <button
                                                    className="device-group-action-btn delete"
                                                    onClick={(e) => { e.stopPropagation(); setDeleteTarget({ type: 'group', item: group }); }}
                                                    title="Supprimer le groupe"
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                            <span className={`device-group-chevron ${isExpanded ? 'expanded' : ''}`}>
                                                ▼
                                            </span>
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div className="device-group-content">
                                            {groupDevices.length > 0 ? (
                                                <div className="devices-grid">
                                                    {groupDevices.map((device) => (
                                                        <div key={device.id} className="device-card">
                                                            <div className="device-card-icon">📱</div>
                                                            <div className="device-card-info">
                                                                <h4 className="device-card-name">{device.name}</h4>
                                                                <p className="device-card-description">
                                                                    {device.description || "Aucune description"}
                                                                </p>
                                                                <p className="device-card-id">
                                                                    ID: <code>{device.id}</code>
                                                                </p>
                                                                {device.project_id ? (
                                                                    <span className="device-card-project">
                                                                        <span className="device-card-project-icon">🎯</span>
                                                                        {projects.find(project => project.id === device.project_id)?.name || "Projet inconnu"}
                                                                    </span>
                                                                ) : (
                                                                    <span className="device-card-project no-project">
                                                                        <span className="device-card-project-icon">○</span>
                                                                        Aucun projet
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="device-card-actions">
                                                                <button
                                                                    className="device-card-btn edit"
                                                                    onClick={() => openEditDeviceModal(device)}
                                                                >
                                                                    ✏️
                                                                </button>
                                                                <button
                                                                    className="device-card-btn delete"
                                                                    onClick={() => setDeleteTarget({ type: 'device', item: device })}
                                                                >
                                                                    🗑️
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="devices-empty-group">
                                                    <span>📭</span>
                                                    <p>Aucun device dans ce groupe</p>
                                                    <button
                                                        className="devices-add-btn"
                                                        onClick={() => openAddDeviceModal(group.id)}
                                                    >
                                                        Ajouter un device
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* État vide */}
                {!isLoading && filteredGroups.length === 0 && (
                    <div className="devices-empty">
                        <div className="devices-empty-icon">📭</div>
                        <h2 className="devices-empty-title">
                            {search ? 'Aucun résultat' : 'Aucun groupe'}
                        </h2>
                        <p className="devices-empty-text">
                            {search
                                ? `Aucun groupe ne correspond à "${search}"`
                                : 'Commencez par créer un groupe pour organiser vos devices'
                            }
                        </p>
                        {!search && (
                            <button className="devices-create-btn" onClick={openAddGroupModal}>
                                <span>📁</span>
                                Créer mon premier groupe
                            </button>
                        )}
                    </div>
                )}

                {/* Modal Groupe */}
                {showGroupModal && (
                    <div className="modal-overlay" onClick={() => setShowGroupModal(false)}>
                        <div className="modal-card form-modal" onClick={(e) => e.stopPropagation()}>
                            <button className="modal-close" onClick={() => setShowGroupModal(false)}>✕</button>
                            <div className="modal-icon info">📁</div>
                            <h2 className="modal-title">
                                {editingGroup ? 'Modifier le groupe' : 'Nouveau groupe'}
                            </h2>
                            <form onSubmit={handleGroupSubmit} className="modal-form">
                                <div className="form-group">
                                    <label>Nom du groupe</label>
                                    <input
                                        type="text"
                                        value={groupForm.name}
                                        onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                                        placeholder="Ex: Hall d'accueil"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Description</label>
                                    <textarea
                                        value={groupForm.description}
                                        onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                                        placeholder="Description du groupe..."
                                        rows={3}
                                    />
                                </div>
                                <div className="modal-actions">
                                    <button type="button" className="modal-btn modal-btn-cancel" onClick={() => setShowGroupModal(false)}>
                                        Annuler
                                    </button>
                                    <button type="submit" className="modal-btn modal-btn-confirm primary">
                                        {editingGroup ? 'Modifier' : 'Créer'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Modal Device */}
                {showDeviceModal && (
                    <div className="modal-overlay" onClick={() => setShowDeviceModal(false)}>
                        <div className="modal-card form-modal" onClick={(e) => e.stopPropagation()}>
                            <button className="modal-close" onClick={() => setShowDeviceModal(false)}>✕</button>
                            <div className="modal-icon info">📱</div>
                            <h2 className="modal-title">
                                {editingDevice ? 'Modifier le device' : 'Nouveau device'}
                            </h2>
                            <form onSubmit={handleDeviceSubmit} className="modal-form">
                                <div className="form-group">
                                    <label>Nom du device</label>
                                    <input
                                        type="text"
                                        value={deviceForm.name}
                                        onChange={(e) => setDeviceForm({ ...deviceForm, name: e.target.value })}
                                        placeholder="Ex: Borne entrée principale"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Description</label>
                                    <textarea
                                        value={deviceForm.description}
                                        onChange={(e) => setDeviceForm({ ...deviceForm, description: e.target.value })}
                                        placeholder="Description du device..."
                                        rows={3}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Groupe</label>
                                    <select
                                        required
                                        value={deviceForm.device_group_id}
                                        onChange={(e) => setDeviceForm({ ...deviceForm, device_group_id: e.target.value })}
                                    >
                                        <option value="">-- Aucun groupe --</option>
                                        {deviceGroups.map((group) => (
                                            <option key={group.id} value={group.id}>
                                                {group.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Projet</label>
                                    <select
                                        value={deviceForm.project_id}
                                        onChange={(e) => setDeviceForm({ ...deviceForm, project_id: e.target.value })}
                                    >
                                        <option value="">-- Aucun projet --</option>
                                        {projects.map((project) => (
                                            <option key={project.id} value={project.id}>
                                                {project.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="modal-actions">
                                    <button type="button" className="modal-btn modal-btn-cancel" onClick={() => setShowDeviceModal(false)}>
                                        Annuler
                                    </button>
                                    <button type="submit" className="modal-btn modal-btn-confirm primary">
                                        {editingDevice ? 'Modifier' : 'Créer'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Modal de confirmation de suppression */}
                {deleteTarget && (
                    <ConfirmationModal
                        title={deleteTarget.type === 'group' ? 'Supprimer le groupe ?' : 'Supprimer le device ?'}
                        icon="🗑️"
                        iconType="danger"
                        confirmText="Supprimer"
                        cancelText="Annuler"
                        onConfirm={handleDelete}
                        onCancel={() => setDeleteTarget(null)}
                    >
                        Êtes-vous sûr de vouloir supprimer <strong>"{deleteTarget.item.name}"</strong> ?
                        {deleteTarget.type === 'group' && getDevicesByGroup(deleteTarget.item.id).length > 0 && (
                            <><br /><br />⚠️ Ce groupe contient {getDevicesByGroup(deleteTarget.item.id).length} device(s).</>
                        )}
                    </ConfirmationModal>
                )}
            </div>
        </div>
    );
}
